import React, { useState, useEffect, useCallback } from 'react';
import { formatInTenantTz } from '../../lib/datetime';
import { getErrorMessage } from '../../lib/errorMessage';
import { displayCustomerName, safePhone, safeEmail } from '../../lib/customerIdentity';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { useToast } from '../../hooks/use-toast';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAuth } from '../../contexts/AuthContext';
import { canManageNotifications } from '../../lib/role';
import ContactManager from '../CRM/ContactManager';
import PushNotificationCenter from '../CRM/PushNotificationCenter';
import {
  Search,
  Users,
  MessageSquare,
  Phone,
  Mail,
  MapPin,
  Star,
  Filter,
  Plus,
  Trash2,
  RefreshCw,
  Calendar,
  TrendingUp,
  ContactRound,
  Bell
} from 'lucide-react';

const Customers = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [journeyData, setJourneyData] = useState(null);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    status: 'active'
  });
  const [emailData, setEmailData] = useState({
    subject: '',
    body: ''
  });
  const { toast } = useToast();
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { currentBusiness } = useBusiness();
  const { user } = useAuth();
  const businessId = currentBusiness?.brandId;
  // P1 RBAC/UI guard — Push Notifications is management-only (mirrors backend Slice A policy).
  // Operator/analyst/it/customer/unknown must NOT see the tab or mount PushNotificationCenter
  // (backend already 403s /notifications/history for them — this stops the red 403 banner).
  const canManageNotifs = canManageNotifications(user?.role);

  const fetchCustomers = useCallback(async () => {
    if (!businessId) return;
    setIsLoading(true);
    try {
      const response = await makeAuthenticatedRequest(
        `/api/v1/customers/business/${businessId}?limit=200${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`
      );
      const data = await response.json();
      const list = Array.isArray(data) ? data : data?.customers || [];
      // Identity guards (shared lib): normal Business UI must NEVER render a raw internal identifier
      // (user-*, cust_*, staff_*, biz_*, a UUID or a Firebase-style uid) as friendly customer data.
      // Customers created via voice/AI carry a synthetic `user-<uid>` in the phone field — reject it.
      setCustomers(list.map(c => ({
        ...c,
        id: c.id || c.customer_id,
        name: displayCustomerName(c),      // friendly name; 'Customer' fallback — never a raw id / "Unknown"
        phone: safePhone(c.phone),         // null -> UI renders "Not provided" (never a raw user-* id)
        email: safeEmail(c.email),         // null -> UI renders "Not provided"
        totalTickets: c.total_tickets || 0,
        // Resolved count is NOT part of the customer list payload. It is derived
        // per-customer from real journey analytics (summary.resolved) once a customer
        // is selected. Honest null at list level (rendered as "—").
        resolvedTickets: null,
        satisfactionScore: c.satisfaction_score ?? '—',
        // Backend customer record does not expose a preferred channel or location.
        // Use real fields when present, otherwise honest fallback (no fabricated defaults).
        preferredChannel: c.preferred_channel || null,
        location: c.location || c.city || '—',
        lastInteraction: c.last_interaction || c.created_at,
      })));
    } catch (err) {
      toast({ title: 'Failed to load customers', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [businessId, searchTerm, makeAuthenticatedRequest, toast]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const fetchCustomerJourney = useCallback(async (customer) => {
    if (!customer || !businessId) return;
    const custId = customer.customer_id || customer.id;
    if (!custId) return;
    setJourneyLoading(true);
    setJourneyData(null);
    try {
      const resp = await makeAuthenticatedRequest(
        `/api/v1/analytics/customer-journey/${custId}?business_id=${businessId}`
      );
      if (resp.ok) {
        const data = await resp.json();
        setJourneyData(data);
      }
    } catch (err) {
      // Fail silently — customer info still shown
    } finally {
      setJourneyLoading(false);
    }
  }, [businessId, makeAuthenticatedRequest]);

  useEffect(() => {
    if (selectedCustomer) fetchCustomerJourney(selectedCustomer);
    else setJourneyData(null);
  }, [selectedCustomer, fetchCustomerJourney]);

  // Resolve missing contact fields — uses the individual customer GET endpoint (db.business_customers)
  const _resolveContact = useCallback(async (customer) => {
    const custId = customer.customer_id || customer.id;
    if (!custId || !businessId) return { phone: '', email: '' };
    try {
      const resp = await makeAuthenticatedRequest(
        `/api/v1/customers/business/${businessId}/${custId}`
      );
      if (resp.ok) {
        const d = await resp.json();
        if (d.phone || d.email) {
          return { phone: d.phone || '', email: d.email || '' };
        }
      }
    } catch (_) {}
    return { phone: '', email: '' };
  }, [businessId, makeAuthenticatedRequest]);

  // Customer interaction functions
  const handleCallCustomer = async (customer) => {
    const name = customer.name || 'Customer';
    const custId = customer.customer_id || customer.id;

    // Find the most recent conversation for this customer to use as the call context.
    // Priority 1: conversation event in the already-loaded journey timeline.
    // Priority 2: fetch from the business conversations list filtered by customer_id.
    let convId = null;

    if (journeyData?.timeline?.length) {
      const convEvent = [...journeyData.timeline]
        .filter(e => e.type === 'conversation' || e.channel === 'chat' || e.channel === 'ai_chat')
        .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))[0];
      if (convEvent?.id) convId = convEvent.id;
    }

    if (!convId && businessId) {
      try {
        const r = await makeAuthenticatedRequest(`/api/v1/conversations/business/${businessId}`);
        if (r.ok) {
          const d = await r.json();
          const custConv = (d.conversations || [])
            .filter(c => c.customer_id === custId)
            .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0];
          if (custConv?.id) convId = custConv.id;
        }
      } catch (_) {}
    }

    if (!convId) {
      toast({
        title: "No Conversation Found",
        description: `${name} has no conversation history. Start an AI Chat with this customer first, then try calling.`,
        variant: "destructive",
      });
      return;
    }

    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/conversations/${convId}/call`, {
        method: 'POST',
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success) {
        const initiated = data.status === 'initiated';
        toast({
          title: initiated ? 'Call Initiated' : 'Call Logged',
          description: data.message || `${initiated ? 'Calling' : 'Call recorded for'} ${name}`,
        });
      } else {
        throw new Error(data.detail || `HTTP ${resp.status}`);
      }
    } catch (err) {
      toast({ title: "Call Failed", description: err.message, variant: "destructive" });
    }
  };

  const handleMessageCustomer = (customer) => {
    const custId = customer.customer_id || customer.id;
    if (!custId) {
      toast({ title: "Cannot Open Chat", description: "Missing: customer_id. Cannot start conversation.", variant: "destructive" });
      return;
    }
    sessionStorage.setItem('newConversationCustomer', JSON.stringify({
      id: custId,
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      company: customer.company,
    }));
    navigate('/business/tickets');
  };

  const handleEmailCustomer = async (customer) => {
    let email = (customer.email || '').trim();
    const name = customer.name || 'Customer';

    if (!email) {
      toast({ title: "Resolving email…", description: "Looking up contact details." });
      const resolved = await _resolveContact(customer);
      email = resolved.email;
    }

    if (!email) {
      toast({
        title: "No Email Address",
        description: `${name} has no email on record. Add an email address to send this message.`,
        variant: "destructive",
      });
      return;
    }

    setSelectedCustomer({ ...customer, email });
    setEmailData({ subject: '', body: '' });
    setEmailComposerOpen(true);
  };

  const handleSendEmail = async () => {
    if (!selectedCustomer || !emailData.subject || !emailData.body) {
      toast({ title: "Validation Error", description: "Please fill in subject and message body", variant: "destructive" });
      return;
    }
    if (!selectedCustomer.email) {
      toast({ title: "No Email Address", description: "Customer has no email address on record.", variant: "destructive" });
      return;
    }
    try {
      const resp = await makeAuthenticatedRequest('/api/v1/emails/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: [selectedCustomer.email],
          subject: emailData.subject,
          message: emailData.body,
          business_id: businessId,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      toast({ title: "Email Sent", description: `Email sent to ${selectedCustomer.name} (${selectedCustomer.email || 'Not provided'})` });
      setEmailComposerOpen(false);
      setEmailData({ subject: '', body: '' });
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to send email", variant: "destructive" });
    }
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email) {
      toast({ title: "Missing Information", description: "Name and email are required", variant: "destructive" });
      return;
    }
    if (!businessId) {
      toast({ title: "Error", description: "No business session found", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/customers/business/${businessId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newCustomer.name,
          email: newCustomer.email,
          phone: newCustomer.phone || undefined,
          notes: newCustomer.company ? `Company: ${newCustomer.company}` : undefined,
          status: newCustomer.status || 'active',
        }),
      });
      const created = await resp.json();
      if (!resp.ok) throw new Error(created.detail || `HTTP ${resp.status}`);
      setCustomers(prev => [{
        ...created,
        id: created.id || created.customer_id,
        totalTickets: 0,
        resolvedTickets: null,
        satisfactionScore: created.satisfaction_score ?? '—',
        preferredChannel: created.preferred_channel || null,
        location: created.location || created.city || '—',
        lastInteraction: created.created_at,
      }, ...prev]);
      setNewCustomer({ name: '', email: '', phone: '', company: '', status: 'active' });
      setAddCustomerOpen(false);
      toast({ title: "Customer Added", description: `${created.name} has been added successfully` });
    } catch (err) {
      const detail = getErrorMessage(err, 'Failed to add customer');
      toast({ title: "Error", description: detail, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCustomer = async (customer) => {
    if (!businessId || !customer?.id) return;
    if (!window.confirm(`Delete "${customer.name}"? This cannot be undone.`)) return;
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/customers/business/${businessId}/${customer.id}`, { method: 'DELETE' });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      setCustomers(prev => prev.filter(c => c.id !== customer.id));
      if (selectedCustomer?.id === customer.id) setSelectedCustomer(null);
      toast({ title: "Customer Deleted", description: `${customer.name} has been removed` });
    } catch (err) {
      toast({ title: "Error", description: err.message || "Failed to delete customer", variant: "destructive" });
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (customer.company && customer.company.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'voice':
        return <Phone className="h-4 w-4" />;
      case 'text':
        return <MessageSquare className="h-4 w-4" />;
      case 'email':
        return <Mail className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getChannelColor = (channel) => {
    switch (channel) {
      case 'voice':
        return 'bg-green-100 text-green-800';
      case 'text':
        return 'bg-blue-100 text-blue-800';
      case 'email':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="flex-1 h-full bg-gradient-to-br from-white via-green-50 to-purple-50">
      {/* Header */}
      <div className="p-6 bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Customer Management</h1>
            <p className="text-gray-600">Manage customer relationships and interaction history</p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={fetchCustomers} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Advanced Filters</DialogTitle>
                  <DialogDescription>
                    Filter customers by various criteria
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Status</label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setFiltersOpen(false)}>
                      Close
                    </Button>
                    <Button onClick={() => setFiltersOpen(false)}>
                      Apply Filters
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={addCustomerOpen} onOpenChange={setAddCustomerOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Customer
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Add New Customer</DialogTitle>
                  <DialogDescription>
                    Enter customer information to add them to your database
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Customer Name *</label>
                    <Input
                      placeholder="Enter full name"
                      value={newCustomer.name}
                      onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email Address *</label>
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Phone Number</label>
                    <Input
                      placeholder="Enter phone number"
                      value={newCustomer.phone}
                      onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Company</label>
                    <Input
                      placeholder="Enter company name"
                      value={newCustomer.company}
                      onChange={(e) => setNewCustomer(prev => ({ ...prev, company: e.target.value }))}
                    />
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setAddCustomerOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddCustomer}>
                      Add Customer
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Customers</p>
                  <p className="text-2xl font-bold text-gray-900">{customers.length}</p>
                </div>
                <Users className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active</p>
                  <p className="text-2xl font-bold text-gray-900">{customers.filter(c => c.status === 'active').length}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">With Interactions</p>
                  <p className="text-2xl font-bold text-gray-900">{customers.filter(c => (c.totalTickets || 0) > 0).length}</p>
                </div>
                <Star className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Managed</p>
                  <p className="text-2xl font-bold text-gray-900">{customers.filter(c => c.is_managed).length}</p>
                </div>
                <MessageSquare className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="customers" className="flex-1 flex flex-col">
        <div className="p-4 bg-white/80 backdrop-blur-sm border-b border-gray-200">
          <TabsList className={`grid w-full max-w-md ${canManageNotifs ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <TabsTrigger value="customers">
              <Users className="h-4 w-4 mr-2" />
              Customers
            </TabsTrigger>
            <TabsTrigger value="contacts">
              <ContactRound className="h-4 w-4 mr-2" />
              CRM Contacts
            </TabsTrigger>
            {/* Push Notifications is management-only — hidden for operator/analyst/it/etc. */}
            {canManageNotifs && (
              <TabsTrigger value="notifications">
                <Bell className="h-4 w-4 mr-2" />
                Push Notifications
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* Customers Tab */}
        <TabsContent value="customers" className="flex-1 flex m-0">
          {/* Customer List */}
          <div className="w-1/2 border-r border-gray-200 bg-white/70 backdrop-blur-sm min-w-0">
          <div className="p-4">
            <div className="space-y-4">
              {isLoading ? (
                <div className="text-center py-12">
                  <RefreshCw className="h-10 w-10 text-gray-400 mx-auto mb-4 animate-spin" />
                  <p className="text-gray-500">Loading customers...</p>
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
                  <p className="text-gray-500">Add a customer to get started</p>
                </div>
              ) : (
                filteredCustomers.map((customer) => (
                  <Card
                    key={customer.id}
                    className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                      selectedCustomer?.id === customer.id
                        ? 'ring-2 ring-purple-500 bg-purple-50'
                        : 'bg-white hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedCustomer(customer)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className="bg-gradient-to-r from-blue-400 to-purple-500 text-white font-semibold">
                              {customer.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h4 className="font-semibold text-gray-900">{customer.name}</h4>
                            <p className="text-sm text-gray-600">{customer.email}</p>
                            <div className="flex items-center space-x-1 mt-1">
                              <MapPin className="h-3 w-3 text-gray-400" />
                              <span className="text-xs text-gray-500">{customer.location}</span>
                            </div>
                          </div>
                        </div>
                        {customer.is_managed && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); handleDeleteCustomer(customer); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                        <div className="text-center p-2 bg-blue-50 rounded">
                          <div className="font-semibold text-blue-700">{customer.totalTickets}</div>
                          <div className="text-blue-600 text-xs">Total Tickets</div>
                        </div>
                        <div className="text-center p-2 bg-green-50 rounded">
                          <div className="font-semibold text-green-700">{customer.resolvedTickets ?? '—'}</div>
                          <div className="text-green-600 text-xs">Resolved</div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <Badge className={getChannelColor(customer.preferredChannel)}>
                          {getChannelIcon(customer.preferredChannel)}
                          <span className="ml-1 capitalize">{customer.preferredChannel || 'Unknown'}</span>
                        </Badge>
                        <div className="flex items-center space-x-1">
                          <Star className="h-4 w-4 text-yellow-500 fill-current" />
                          <span className="text-sm font-medium">{customer.satisfactionScore}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="flex-1 bg-white">
          {selectedCustomer ? (
            <div className="h-full flex flex-col">
              {/* Customer Header */}
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-white to-gray-50">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <Avatar className="h-16 w-16">
                      <AvatarFallback className="bg-gradient-to-r from-blue-400 to-purple-500 text-white text-xl font-bold">
                        {selectedCustomer.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{selectedCustomer.name}</h2>
                      <p className="text-gray-600">{selectedCustomer.email || 'Not provided'}</p>
                      <div className="flex items-center space-x-2 mt-2">
                        <Badge className={getChannelColor(selectedCustomer.preferredChannel)}>
                          {getChannelIcon(selectedCustomer.preferredChannel)}
                          <span className="ml-1 capitalize">{selectedCustomer.preferredChannel || 'Unknown'}</span>
                        </Badge>
                        <Badge variant="outline">
                          <Star className="h-3 w-3 mr-1 text-yellow-500 fill-current" />
                          {selectedCustomer.satisfactionScore}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleCallCustomer(selectedCustomer)}
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      Call
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleMessageCustomer(selectedCustomer)}
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Message
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEmailCustomer(selectedCustomer)}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Email
                    </Button>
                  </div>
                </div>

                {/* Customer Stats */}
                <div className="grid grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{selectedCustomer.totalTickets}</div>
                    <div className="text-sm text-gray-600">Total Interactions</div>
                  </div>
                  <div className="text-center">
                    {/* Resolved + Success Rate derived from real journey analytics
                        (GET /analytics/customer-journey summary). Honest "—" until loaded. */}
                    <div className="text-2xl font-bold text-green-600">
                      {journeyData?.summary?.resolved ?? '—'}
                    </div>
                    <div className="text-sm text-gray-600">Resolved Issues</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {journeyData?.summary && journeyData?.total_events > 0
                        ? `${Math.round((journeyData.summary.resolved / journeyData.total_events) * 100)}%`
                        : '—'}
                    </div>
                    <div className="text-sm text-gray-600">Success Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{selectedCustomer.satisfactionScore}</div>
                    <div className="text-sm text-gray-600">Satisfaction</div>
                  </div>
                </div>
              </div>

              {/* Customer Details Tabs */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid lg:grid-cols-2 gap-8">
                  {/* Contact Information */}
                  <Card className="bg-gray-50 border-0">
                    <CardHeader>
                      <CardTitle className="text-lg">Contact Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <Phone className="h-5 w-5 text-gray-400" />
                        <div>
                          <div className="font-medium">{selectedCustomer.phone || 'Not provided'}</div>
                          <div className="text-sm text-gray-600">Primary Phone</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Mail className="h-5 w-5 text-gray-400" />
                        <div>
                          <div className="font-medium">{selectedCustomer.email || 'Not provided'}</div>
                          <div className="text-sm text-gray-600">Email Address</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <MapPin className="h-5 w-5 text-gray-400" />
                        <div>
                          <div className="font-medium">{selectedCustomer.location}</div>
                          <div className="text-sm text-gray-600">Location</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Calendar className="h-5 w-5 text-gray-400" />
                        <div>
                          <div className="font-medium">
                            {selectedCustomer.lastInteraction && !isNaN(new Date(selectedCustomer.lastInteraction))
                              ? formatInTenantTz(selectedCustomer.lastInteraction, { dateStyle: 'medium' })
                              : '—'}
                          </div>
                          <div className="text-sm text-gray-600">Last Interaction</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Customer Journey — real data from GET /analytics/customer-journey */}
                  <Card className="bg-gray-50 border-0">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-blue-500" />
                        Customer Journey
                        {journeyData && (
                          <span className="text-sm font-normal text-gray-500 ml-2">
                            {journeyData.total_events} events
                          </span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {journeyLoading ? (
                        <div className="flex items-center justify-center py-6 text-gray-500">
                          <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                          Loading journey...
                        </div>
                      ) : journeyData?.timeline?.length > 0 ? (
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                          {journeyData.timeline.slice(0, 10).map((event, idx) => {
                            const typeColors = {
                              ai_chat: 'bg-blue-500',
                              ai_voice: 'bg-teal-500',
                              escalation: 'bg-orange-500',
                              ticket: 'bg-purple-500',
                            };
                            const typeLabels = {
                              ai_chat: 'AI Chat',
                              ai_voice: 'AI Voice',
                              escalation: 'Escalation',
                              ticket: 'Ticket',
                            };
                            const statusBg = event.status === 'resolved' || event.status === 'auto_resolved'
                              ? 'text-green-700 bg-green-100'
                              : event.status === 'escalated' ? 'text-orange-700 bg-orange-100'
                              : 'text-gray-700 bg-gray-100';
                            return (
                              <div key={idx} className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                                <div className={`w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0 ${typeColors[event.type] || 'bg-gray-400'}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm text-gray-900">
                                      {typeLabels[event.type] || event.type}
                                    </span>
                                    {event.status && (
                                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${statusBg}`}>
                                        {event.status.replace(/_/g, ' ')}
                                      </span>
                                    )}
                                  </div>
                                  {(event.reason || event.intent || event.context_summary) && (
                                    <div className="text-xs text-gray-500 truncate mt-0.5">
                                      {event.reason || event.intent || event.context_summary}
                                    </div>
                                  )}
                                  <div className="text-xs text-gray-400 mt-0.5">
                                    {event.timestamp ? formatInTenantTz(event.timestamp) : '—'}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {journeyData.total_events > 10 && (
                            <div className="text-center text-xs text-gray-400 py-2">
                              Showing 10 of {journeyData.total_events} events
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-400">
                          <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No journey events found for this customer</p>
                        </div>
                      )}
                      {journeyData?.summary && (
                        <div className="mt-3 pt-3 border-t grid grid-cols-4 gap-2 text-center">
                          {[
                            { label: 'Chats', value: journeyData.summary.ai_chat },
                            { label: 'Voice', value: journeyData.summary.ai_voice },
                            { label: 'Escalations', value: journeyData.summary.escalations },
                            { label: 'Resolved', value: journeyData.summary.resolved },
                          ].map((s, i) => (
                            <div key={i}>
                              <div className="text-sm font-bold text-gray-800">{s.value ?? 0}</div>
                              <div className="text-xs text-gray-500">{s.label}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Select a customer</h3>
                <p className="text-sm">Choose a customer from the list to view their details and history</p>
              </div>
            </div>
          )}
        </div>
        </TabsContent>

        {/* CRM Contacts Tab */}
        <TabsContent value="contacts" className="flex-1 m-0">
          <ContactManager />
        </TabsContent>

        {/* Push Notifications Tab */}
        {canManageNotifs && (
          <TabsContent value="notifications" className="flex-1 m-0">
            <PushNotificationCenter canManage={canManageNotifs} />
          </TabsContent>
        )}
      </Tabs>

      {/* Email Composer Dialog */}
      <Dialog open={emailComposerOpen} onOpenChange={setEmailComposerOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Compose Email</DialogTitle>
            <DialogDescription>
              {selectedCustomer && `Sending email to ${selectedCustomer.name} (${selectedCustomer.email || 'Not provided'})`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="email-subject">Subject</Label>
              <Input
                id="email-subject"
                value={emailData.subject}
                onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                placeholder="Enter email subject"
              />
            </div>
            <div>
              <Label htmlFor="email-body">Message</Label>
              <Textarea
                id="email-body"
                value={emailData.body}
                onChange={(e) => setEmailData({...emailData, body: e.target.value})}
                placeholder="Enter email message"
                rows={10}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setEmailComposerOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendEmail}>
                <Mail className="h-4 w-4 mr-2" />
                Send Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;